import { GlobalColor } from "@builder/components/general/ColorInput";
import availableFonts from "@builder/fonts/availableFonts";
import { BuilderSliceActions } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch } from "@builder/store/store";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { layoutStyles } from "./starterTemplates/layoutStyles";
import { applyOnboardThemeEdits, defaultEdits } from "./helpers/batchEditing";
import { useAppSelector } from "@builder/store/store";
import { useRenameCompanyMutation, useUpdateCompanyMutation } from "@builder/store/slices/dataApiSlices/companiesApiSlice";
import { useUpdateOnboardingCompleteMutation } from "@builder/store/slices/dataApiSlices/companiesApiSlice";
import { useUpdateUserMutation } from "@builder/store/slices/dataApiSlices/userApiSlice";
import { useUpdateCompanyLogoMutation } from "@builder/store/slices/dataApiSlices/companiesApiSlice";
import useDebounce from "@builder/hooks/useDebounce";
import { usePublishBuildMutation, useSaveBuildMutation } from "@builder/store/slices/BuildsApiSlice/buildsApiSlice";
import toast from "react-hot-toast";
import { useCreateStripeCustomerMutation } from "@builder/store/slices/dataApiSlices/stripeApiSlice";
import { setUser, setCompany } from "@builder/store/slices/storeDataSlice";
import { Outlet } from "@builder/types/Outlet.type";
import { useOnboardCompanyMutation } from "@builder/store/slices/dataApiSlices/companiesApiSlice";
import { useLazySuggestTaxRateQuery } from "@builder/store/slices/dataApiSlices/taxApiSlice";
import { useLazyGetAllPlansQuery } from "@builder/store/slices/dataApiSlices/planApiSlice";
import { Country } from "country-state-city";
import getSymbolFromCurrency from "currency-symbol-map";

export interface POSOnboardSetup {
    colors?: {
        globalColors: GlobalColor[];
        selectedColorPaletteId?: string;
    };
    selectedLayoutId?: string;
    selectedFontName?: string;
    organizationName?: string;
    firstName?: string;
    lastName?: string;
    organizationCategory?: {
        category: string;
        details?: string;
    };
    referralSource?: {
        source: string;
        details?: string;
    };
    logoUrl?: string;
    currentStepIdx: number;
    outlet?: Partial<Outlet>;
}

export const useApplyOnboardSetup = ({
    setup,
    setSetup,
    enabled = true,
    isMobile = false
}: {
    setup: POSOnboardSetup;
    setSetup: Dispatch<SetStateAction<POSOnboardSetup>>;
    enabled: boolean;
    isMobile: boolean;
}) => {
    const dispatch = useAppDispatch();

    const { company, user } = useAppSelector(state => state.storeData);

    const [renameCompany, { isLoading: renamingCompany }] = useRenameCompanyMutation();
    const [updateUser] = useUpdateUserMutation();
    const [updateCompanyLogo, { isLoading: updatingLogo }] = useUpdateCompanyLogoMutation();
    const [updateOnboardingComplete, { isLoading: updatingRegistrationComplete }] = useUpdateOnboardingCompleteMutation();
    const [createStripeCustomer, { isLoading: creatingStripeCustomer }] = useCreateStripeCustomerMutation();
    const [onboardCompany, { isLoading: isCreatingCompany }] = useOnboardCompanyMutation();
    const [updateCompany, { isLoading: updatingCompany }] = useUpdateCompanyMutation();
    const [suggestTaxRate] = useLazySuggestTaxRateQuery();
    const [getAllPlans] = useLazyGetAllPlansQuery();

    const debouncedOrgName = useDebounce(setup.organizationName, 1000);
    const debouncedUserNames = useDebounce({ firstName: setup.firstName, lastName: setup.lastName }, 600);
    const [isFinalScreen, setIsFinalScreen] = useState<boolean>(false);

    const isDebouncingOrgName = !!(setup.organizationName && setup.organizationName !== debouncedOrgName);

    const isDebouncingNames = !!(
        (setup.firstName && setup.firstName !== debouncedUserNames.firstName) ||
        (setup.lastName && setup.lastName !== debouncedUserNames.lastName)
    );

    useEffect(() => {
        if (!enabled) return;
        if (setup.firstName || setup.lastName) return;

        const isGoogleAccount = (user as any)?.userIdAuth0?.startsWith("google-oauth2");
        if (!isGoogleAccount) return;

        const auth0Data = (user as any)?.auth0_user_data;
        if (!auth0Data?.name) return;

        const nameParts = auth0Data.name.trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        if (firstName || lastName) {
            setSetup(prev => ({
                ...prev,
                ...(firstName && !prev.firstName ? { firstName } : {}),
                ...(lastName && !prev.lastName ? { lastName } : {})
            }));
        }
    }, [enabled, user]);

    const combinedProcessingOfUserModifications = renamingCompany || updatingLogo || isDebouncingOrgName || isDebouncingNames;

    const combinedProcessingofAnyChanges =
        combinedProcessingOfUserModifications || updatingRegistrationComplete || creatingStripeCustomer || isCreatingCompany || updatingCompany;

    // publish build automatically on mobile:
    const build = useAppSelector(state => state.builder.build);
    // const companyId = useAppSelector(state => state.storeData.company?._id || "");
    const companyId = useAppSelector(state => state.builder.build.companyId);
    const [publishBuild, { isLoading: publishBuildLoading, isError: publishBuildError }] = usePublishBuildMutation();
    const [saveBuild, { isLoading: saveBuildLoading, isError: saveBuildError }] = useSaveBuildMutation();
    const didJustPublishOrSaveRef = useRef<boolean>(false);

    useEffect(() => {
        if (
            !enabled ||
            !isFinalScreen ||
            publishBuildLoading ||
            saveBuildLoading ||
            didJustPublishOrSaveRef.current ||
            combinedProcessingofAnyChanges ||
            !company?.registrationCompleted
        ) {
            return;
        }

        const autoPublishBuild = async () => {
            didJustPublishOrSaveRef.current = true;
            await publishBuild({
                companyId: companyId ?? "",
                build,
                publishDetails: {
                    version: "1.0.0",
                    changelog: "",
                    updateSettings: {
                        notifyUser: false,
                        autoUpdate: false,
                        scheduleTime: undefined
                    }
                }
            }).unwrap();
        };

        // const autoSaveBuild = async () => {
        //     didJustPublishOrSaveRef.current = true;
        //     await saveBuild({ companyId, build }).unwrap();
        //     console.log("savedBuild!");
        // };

        autoPublishBuild();

        // if (isMobile) {
        //     autoPublishBuild();
        // } else {
        //     autoSaveBuild();
        // }

        if (publishBuildError) {
            toast.error("Failed to publish build");
        } else if (saveBuildError) {
            toast.error("Failed to save build");
        }
    }, [
        enabled,
        isMobile,
        publishBuild,
        setup,
        combinedProcessingofAnyChanges,
        isFinalScreen,
        publishBuildLoading,
        saveBuild,
        saveBuildLoading,
        company?.registrationCompleted
    ]);

    const lastRenamed = useRef<string>();

    useEffect(() => {
        if (!enabled) return;
        if (!setup.colors?.globalColors) return;
        dispatch(BuilderSliceActions.overrideBuildGlobalColors(setup.colors.globalColors));
    }, [setup.colors?.globalColors, enabled]);

    useEffect(() => {
        if (!enabled) return;
        if (!setup.selectedFontName) return;
        const fontName = setup.selectedFontName;
        const font = availableFonts.find(f => f.name === fontName);
        if (!font) return;

        dispatch(BuilderSliceActions.addFontToAttributes({ font, asRoot: true }));
        dispatch(BuilderSliceActions.setFontRoots(font));
    }, [setup.selectedFontName, enabled]);

    useEffect(() => {
        if (!enabled) return;
        if (!setup.selectedLayoutId) return;
        const layout = layoutStyles.find(l => l.id === setup.selectedLayoutId);
        if (!layout) return;
        if (layout.themeEdits) {
            applyOnboardThemeEdits(defaultEdits);
            applyOnboardThemeEdits(layout.themeEdits);
        }
    }, [setup.selectedLayoutId, enabled]);

    useEffect(() => {
        if (!enabled) return;
        if (!debouncedOrgName) return;
        if (!company?._id || !user?.organizationId) return;

        const newName = debouncedOrgName.trim();
        if (!newName || company?.name === newName || lastRenamed.current === newName) return;

        lastRenamed.current = newName;

        renameCompany({
            companyId: company._id,
            newName
        }).catch(() => {
            lastRenamed.current = undefined;
        });
    }, [debouncedOrgName, enabled, company, user, renameCompany]);

    useEffect(() => {
        if (!enabled) return;
        if (!company?._id || !user?._id) return;

        const { firstName, lastName } = debouncedUserNames;
        const updates: { firstName?: string; lastName?: string } = {};
        if (firstName && firstName.trim() !== (user.firstName ?? "")) updates.firstName = firstName.trim();
        if (lastName && lastName.trim() !== (user.lastName ?? "")) updates.lastName = lastName.trim();
        if (Object.keys(updates).length === 0) return;

        updateUser({ companyId: company._id, userId: user._id, ...updates })
            .unwrap()
            .then(() => {
                // keep local store user in sync so we don't keep re-sending the same update
                dispatch(setUser({ ...(user as any), ...updates }));
            })
            .catch(() => {});
    }, [debouncedUserNames, enabled, company, user, updateUser, dispatch]);

    const [lastLogo, setLastLogo] = useState<string | undefined>();

    const didMarkRegistrationCompleteRef = useRef<boolean>(false);
    const didCreateCompanyTaxTableAndOutletRef = useRef<boolean>(false);

    useEffect(() => {
        if (!enabled) return;
        if (!setup.logoUrl) return;
        if (!company?._id) return;
        if (lastLogo === setup.logoUrl) return;
        if (!isFinalScreen) return;

        setLastLogo(setup.logoUrl);

        updateCompanyLogo({ companyId: company._id, logoUrl: setup.logoUrl }).catch(() => {
            setLastLogo(undefined);
        });
    }, [setup.logoUrl, enabled, company, updateCompanyLogo]);

    // Create company before finalizing onboarding
    useEffect(() => {
        if (!enabled) return;
        if (!isFinalScreen) return;
        if (!company?._id) return;
        if (company.registrationCompleted) return;
        if (didCreateCompanyTaxTableAndOutletRef.current) return;
        if (isCreatingCompany) return;
        if (updatingCompany) return;
        if (creatingStripeCustomer || updatingRegistrationComplete) return;

        // Validate required fields before proceeding
        const orgName = (debouncedOrgName ?? "").trim();
        const hasOrgName = orgName.length > 0;
        const hasAnyUserName = debouncedUserNames?.firstName?.length && debouncedUserNames.lastName?.length;
        const hasOutletData = !!(
            setup.outlet?.country &&
            setup.outlet?.address &&
            setup.outlet?.city &&
            setup.outlet?.state &&
            setup.outlet?.postCode
        );

        // Early return if required fields are not filled
        if (!hasAnyUserName) return;
        if (isDebouncingNames) return;
        if (!hasOrgName) return;
        if (!hasOutletData) return;

        const createCompanyTaxTableAndOutlet = async () => {
            didCreateCompanyTaxTableAndOutletRef.current = true;

            try {
                // Get default plan by organizationId
                const plans = await getAllPlans(company._id ?? "");
                const defaultPlanId = plans.data && plans.data.length > 0 ? plans.data[0]._id : null;

                //Get tax rate suggestion by country and state
                const taxResult = await suggestTaxRate({
                    companyId: company._id,
                    taxSuggestionQuery: {
                        country: setup.outlet?.country ?? "",
                        state: setup.outlet?.state
                    }
                });

                const suggestedTaxRate = taxResult.data?.suggestedRate || 0;
                const isTaxInclusive = taxResult.data?.inclusive ?? false;

                // Get currency by country
                const currency = Country.getCountryByCode(setup.outlet?.country ?? "")?.currency;

                // Onboard company for organization
                if (user?.organizationId) {
                    await updateCompany({
                        companyId: company._id,
                        company: {
                            address: {
                                country: setup.outlet?.country ?? "",
                                state: setup.outlet?.state ?? "",
                                city: setup.outlet?.city ?? "",
                                address1: setup.outlet?.address ?? "",
                                postCode: setup.outlet?.postCode ?? "",
                                phone: setup.outlet?.phone ?? ""
                            },
                            settings: {
                                currency,
                                currencySymbol: getSymbolFromCurrency(currency || "USD") ?? "",
                                currencyPrefix: getSymbolFromCurrency(currency || "USD") ?? "",
                                platform: "standalone",
                                isTaxInclusive
                            }
                        }
                    }).unwrap();
                    await onboardCompany({
                        organizationId: company._id,
                        onboardCompanyData: {
                            outletName: setup.organizationName ? `${setup.organizationName} Outlet` : "",
                            userOrganizationOwner: true,
                            createTestCompany: false,
                            planId: defaultPlanId ?? "",
                            defaultTax: {
                                name: "Standard Tax",
                                rate: suggestedTaxRate
                            },
                            owner: user as any,
                            company: {
                                name: setup.organizationName ?? "",
                                organizationId: company._id,
                                type: "company",
                                showStartingGuide: false,
                                isDemoCompany: false,
                                logo: setup.logoUrl ?? "",
                                registrationCompleted: true,
                                settings: {
                                    currency,
                                    currencySymbol: getSymbolFromCurrency(currency || "USD") ?? "",
                                    currencyPrefix: getSymbolFromCurrency(currency || "USD") ?? "",
                                    platform: "standalone",
                                    isTaxInclusive
                                },
                                address: {
                                    country: setup.outlet?.country ?? "",
                                    state: setup.outlet?.state ?? "",
                                    city: setup.outlet?.city ?? "",
                                    address1: setup.outlet?.address ?? "",
                                    postCode: setup.outlet?.postCode ?? "",
                                    phone: setup.outlet?.phone ?? ""
                                },
                                shipping: {
                                    country: setup.outlet?.country ?? "",
                                    state: setup.outlet?.state ?? "",
                                    city: setup.outlet?.city ?? "",
                                    address1: setup.outlet?.address ?? "",
                                    postCode: setup.outlet?.postCode ?? "",
                                    phone: setup.outlet?.phone ?? ""
                                }
                            },
                            finalStripeAutoCreateCustomer: true // To help differentiate first company created from onboarding from others
                        }
                    }).unwrap();
                }
            } catch (error) {
                console.error("Failed to create company, tax table, or outlet", error);
                didCreateCompanyTaxTableAndOutletRef.current = false;
            }
        };

        createCompanyTaxTableAndOutlet();
    }, [
        enabled,
        isFinalScreen,
        company,
        debouncedOrgName,
        debouncedUserNames,
        isDebouncingNames,
        setup.outlet,
        updatingCompany,
        isCreatingCompany,
        creatingStripeCustomer,
        updatingRegistrationComplete,
        user?.organizationId,
        onboardCompany,
        suggestTaxRate
    ]);

    // Finalize onboarding: create stripe customer -> mark complete
    useEffect(() => {
        if (!enabled) return;
        if (!isFinalScreen) return;
        if (!company?._id) return;
        if (company.registrationCompleted) return;
        if (didMarkRegistrationCompleteRef.current) return;
        if (creatingStripeCustomer || updatingRegistrationComplete) return;
        // Wait for company, tax table, and outlet to be created first
        if (!didCreateCompanyTaxTableAndOutletRef.current || isCreatingCompany || updatingCompany) return;

        const orgName = (debouncedOrgName ?? "").trim();
        const hasOrgName = orgName.length > 0;
        const hasAnyUserName = debouncedUserNames?.firstName?.length && debouncedUserNames.lastName?.length;

        if (!hasAnyUserName) return;
        if (isDebouncingNames) return;
        if (!hasOrgName) return;

        const createStripeCustomerAndMarkOnboardComplete = async () => {
            didMarkRegistrationCompleteRef.current = true;

            const referralSourceValue =
                setup.referralSource?.source === "other" && setup?.referralSource?.details
                    ? setup.referralSource?.details
                    : setup.referralSource?.source;

            const organizationCategoryValue =
                setup.organizationCategory?.category === "other" && setup.organizationCategory?.details
                    ? setup.organizationCategory.details
                    : setup.organizationCategory?.category;

            try {
                await createStripeCustomer({
                    companyId: company._id,
                    companyName: orgName,
                    name: `${debouncedUserNames.firstName} ${debouncedUserNames.lastName}`,
                    email: user?.email ?? "",
                    organizationCategory: organizationCategoryValue ?? "other",
                    referralSource: referralSourceValue ?? "other"
                }).unwrap();

                await updateOnboardingComplete({ companyId: company._id, registrationCompleted: true }).unwrap();

                // Update local state to trigger saveBuild
                dispatch(setCompany({ ...company, registrationCompleted: true }));
            } catch (error) {
                console.error("Failed to finalize onboarding", error);
                didMarkRegistrationCompleteRef.current = false;
            }
        };

        createStripeCustomerAndMarkOnboardComplete();
    }, [
        enabled,
        isFinalScreen,
        company,
        debouncedOrgName,
        debouncedUserNames,
        isDebouncingNames,
        updateOnboardingComplete,
        createStripeCustomer,
        setup.referralSource,
        setup.organizationCategory,
        user?.email,
        creatingStripeCustomer,
        updatingRegistrationComplete,
        dispatch,
        isCreatingCompany,
        updatingCompany
    ]);

    const isDoneEnjoyingSomeFinalScreenAnimations = useDebounce(isFinalScreen, 4000);
    const isDone = isFinalScreen && !combinedProcessingofAnyChanges && didJustPublishOrSaveRef.current && isDoneEnjoyingSomeFinalScreenAnimations; // make sure they get to see some cool animation at the end even if done processing

    return { isDone, isFinalScreen, setIsFinalScreen };
};
